// app/src/features/layout/useHotkeys.js
// Called after `useTimelineFraming`, whose `jumpTo` the zoom keys call.
import { useEffect, useRef } from "react";
import { topmostMatch } from "../../lib/popupKeys.js";
import { noteShortcut } from "../../lib/telemetry/recentControl.js";

/**
 * Registers the two document keydown listeners once; returns `newQuestionRef`, which AppInner
 * points at `startNewQuestion` so Shift+↑ reaches it.
 *
 * @param {object} deps  the timeline geometry's refs and `scheduleRender`, the framing's `jumpTo`,
 *   the Signal Library drawer's state and the rail panels' setters
 */
export function useHotkeys({
  jumpTo,
  ppdRef,
  panAnimRef,
  vlRef,
  scheduleRender,
  evtScrollRef,
  vPanAnimRef,
  setTsDrawerOpen,
  tsdH,
  setTsdH,
  setLogOpen,
  setChatOpen,
}) {
  // New Question (startNewQuestion, below), for Shift+↑: the handler is registered once.
  const newQuestionRef = useRef(null);
  // The drawer height the Shift+↓ toggle reads: the handler below is registered once, so a
  // closed-over `tsdH` would stay at mount's value and reset a dragged height on toggle.
  const tsdHRef = useRef(tsdH);
  useEffect(() => {
    tsdHRef.current = tsdH;
  }, [tsdH]);
  // Registered in CAPTURE phase so it runs before the auto-fill-chat keydown listener;
  // stopImmediatePropagation keeps matched hotkeys out of the chat input.
  useEffect(() => {
    const handler = (e) => {
      const ae = document.activeElement;
      const tag = ae?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      // Don't hijack keys while a control that owns its own arrow-key navigation is
      // focused: a segmented-control radio (Settings Forecaster/Theme) or an item in
      // an open menu (the composer's Quick/Deep selector). Their roving-focus handlers
      // need those keys — and this handler's capture-phase stopImmediatePropagation
      // (below) would otherwise eat the arrow before their React onKeyDown could run.
      const aeRole = ae?.getAttribute?.("role");
      if (aeRole === "radio" || aeRole === "menuitemradio" || aeRole === "menuitem") return;
      const browserZoom =
        (e.ctrlKey || e.metaKey) &&
        (e.key === "+" || e.key === "=" || e.key === "-" || e.key === "_");
      if (browserZoom) return;
      let handled = false;
      if (e.key === "+" || e.key === "=") {
        jumpTo(Math.min(3000, ppdRef.current * 1.5));
        handled = true;
      } else if (e.key === "-" || e.key === "_") {
        jumpTo(Math.max(0.08, ppdRef.current / 1.5));
        handled = true;
      } else if (e.shiftKey) {
        if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
          noteShortcut("panel.toggle");
        }
        if (e.key === "ArrowUp") {
          newQuestionRef.current?.();
          handled = true;
        } else if (e.key === "ArrowDown") {
          setTsDrawerOpen((v) => {
            if (v && tsdHRef.current === 0) setTsdH(Math.round(window.innerHeight * 0.25));
            return !v;
          });
          handled = true;
        } else if (e.key === "ArrowLeft") {
          setLogOpen((v) => !v);
          handled = true;
        } else if (e.key === "ArrowRight") {
          setChatOpen((v) => !v);
          handled = true;
        }
      } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        const dir = e.key === "ArrowRight" ? 1 : -1;
        if (panAnimRef.current) cancelAnimationFrame(panAnimRef.current);
        const totalPx = 120;
        const dur = 220;
        const start = performance.now();
        const startVL = vlRef.current;
        const dist = (dir * totalPx) / ppdRef.current;
        const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
        const tick = (now) => {
          const t = Math.min(1, (now - start) / dur);
          vlRef.current = startVL + dist * ease(t);
          scheduleRender();
          if (t < 1) panAnimRef.current = requestAnimationFrame(tick);
          else panAnimRef.current = null;
        };
        panAnimRef.current = requestAnimationFrame(tick);
        handled = true;
      } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        const el = evtScrollRef.current;
        if (el) {
          const dir = e.key === "ArrowDown" ? 1 : -1;
          if (vPanAnimRef.current) cancelAnimationFrame(vPanAnimRef.current);
          const totalPx = 120;
          const dur = 220;
          const start = performance.now();
          const startTop = el.scrollTop;
          const dist = dir * totalPx;
          const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
          const tick = (now) => {
            const t = Math.min(1, (now - start) / dur);
            el.scrollTop = startTop + dist * ease(t);
            if (t < 1) vPanAnimRef.current = requestAnimationFrame(tick);
            else vPanAnimRef.current = null;
          };
          vPanAnimRef.current = requestAnimationFrame(tick);
          handled = true;
        }
      }
      if (handled) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    // telemetry-gesture: zoom and pan keys are view navigation; Shift+arrow keys (Shift+↑ New Question) note origin `shortcut` for panel.toggle
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, []);
  useEffect(() => {
    const popupHandler = (e) => {
      const ae = document.activeElement;
      const tag = ae?.tagName;
      const isTextField =
        tag === "TEXTAREA" || (tag === "INPUT" && document.activeElement.type !== "date");
      // A focused button, link or select keeps Enter as its own activation (Attach image, a
      // thumbnail's remove ✕, a dialog's Cancel); the shortcut is for the page around them.
      const ownsEnter =
        tag === "BUTTON" ||
        tag === "A" ||
        tag === "SELECT" ||
        ae?.getAttribute?.("role") === "button";
      if (e.key === "Enter" && (isTextField || ownsEnter)) return;
      if (e.key === "Enter") {
        const btn = topmostMatch(document, "[data-popup-primary]");
        if (btn) {
          e.preventDefault();
          btn.click();
        }
      } else if (e.key === "Escape") {
        const btn = topmostMatch(document, "[data-popup-cancel]");
        if (btn) {
          e.preventDefault();
          btn.click();
        }
      }
    };
    // telemetry-gesture: Enter / Escape click the popup's primary / cancel button, whose data-ui the capture records
    document.addEventListener("keydown", popupHandler);
    return () => document.removeEventListener("keydown", popupHandler);
  }, []);
  return { newQuestionRef };
}
